import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Card,
    CardBody,
    CardTitle,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalBody,
    ModalHeader,
    Spinner,
    Table,
    UncontrolledDropdown,
} from "reactstrap";

import "../../styles/StationLog.css";
import { default as user } from "../../resources/user.svg";
import { default as plus } from "../../resources/plus-svgrepo-com.svg";

import StationLogIssue from "./StationLogIssue";
import { STATIONLOG_URL } from "../../serverConfig";
import {
    today,
    last90,
    formatSiteLst,
} from "../../util/timeUtils";
import { checkMobile } from "../../util/globals";
import StationLogIssueEditor from "./StationLogIssueEditor";
import { RenderContext, RestContext } from "../../App";
import AuthorLink from "./AuthorLink";

export const StationLogContext = createContext(null);

// ---------- CSV helpers ----------

const CSV_HEADERS = ["Type", "Title", "Person", "Date", "Notes"];

const byDateAsc = (a, b) =>
    (a.recordDate < b.recordDate ? -1 : a.recordDate > b.recordDate ? 1 : 0) ||
    a.logId - b.logId;

// Replies come back as a flat list; group them under the issue they belong to.
const groupRepliesByRoot = (replies) => {
    const grouped = {};
    replies.forEach((reply) => {
        if (!grouped[reply.rootEntry]) grouped[reply.rootEntry] = [];
        grouped[reply.rootEntry].push(reply);
    });
    return grouped;
};

// Issues oldest -> newest; each issue is immediately followed by its own
// replies (oldest -> newest), regardless of when the next issue was created.
const getThreadRows = (issues, repliesByRoot, newestFirst = false) =>
    [...issues].sort(newestFirst ? (a, b) => byDateAsc(b, a) : byDateAsc).flatMap((issue) => {
        const replies = [...(repliesByRoot[issue.logId] ?? [])].sort(byDateAsc);
        return [
            {
                key: "entry-" + issue.logId,
                type: "Entry",
                title: issue.title,
                entry: issue,
            },
            ...replies.map((reply) => ({
                key: "reply-" + reply.logId,
                type: "Reply",
                title: issue.title, // replies show their issue's title
                entry: reply,
            })),
        ];
    });

const getCells = (row) => [
    row.type, // Entry / Reply
    row.title, // Title
    row.entry.author, // Person
    formatSiteLst(row.entry.recordDate, row.entry.siteTimezone), // Date
    row.entry.detail, // Notes
];

const csvCell = (value) => {
    let text = String(value ?? "");
    if (/^[=+\-@]/.test(text)) text = "'" + text; // avoid spreadsheet formula injection
    return '"' + text.replace(/"/g, '""') + '"';
};

const getCsvFileName = (issues, filters, selectedSite) => {
    const stripDashes = (d) => String(d ?? "").replace(/-/g, "");
    const abbr =
        selectedSite === "all" || issues.length === 0
            ? "ALL"
            : issues[0].siteAbbr;
    const start = stripDashes(filters.startDate);
    const end = stripDashes(filters.endDate ?? today());
    return `${abbr}${start}-${end}`.replace(/[^\w\-]/g, "_") + ".csv";
};

const exportCsv = (issues, repliesByRoot, filters, selectedSite) => {
    const rows = getThreadRows(issues, repliesByRoot).map(getCells);
    const csv = [CSV_HEADERS, ...rows]
        .map((row) => row.map(csvCell).join(","))
        .join("\r\n");

    const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = getCsvFileName(issues, filters, selectedSite);
    link.click();
    URL.revokeObjectURL(url);
};

// ---------- Components ----------

function StationLog({ selectedSite, siteDetails }) {
    const [issues, setIssues] = useState([]);
    const [editorOpen, setEditorOpen] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState();
    const [recentItems, setRecentItems] = useState([]);
    const [lastPage, setLastPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [atEnd, setAtEnd] = useState(false);
    const [tableView, setTableView] = useState(false);
    const [filters, setFilters] = useState({
        searchString: null,
        startDate: last90(),
        endDate: today(),
        people: new Set(),
        pageSize: 20,
    });
    const [repliesByRoot, setRepliesByRoot] = useState({});
    const requestedRepliesRef = useRef(new Set());
    const repliesGenerationRef = useRef(0);

    const { sendPostRequest } = useContext(RestContext);
    const { renderControlledComponent } = useContext(RenderContext);

    const toggleEditorOpen = () => setEditorOpen(!editorOpen);

    useEffect(() => {
        if (selectedSite !== undefined) {
            setFilters((values) => ({
                ...values,
                searchString: null,
                people: new Set(),
            }));
            setLastPage(0);
            loadRecent();
        }
    }, [selectedSite]);

    useEffect(() => {
        setIssues([]);
        setAtEnd(false);
        if (lastPage === 0) {
            setLoading(true);
            loadIssues();
        } else {
            setLastPage(0);
        }
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        loadIssues();
    }, [lastPage]);

    // Fetch replies (one batch request) for any loaded issues we don't have yet.
    useEffect(() => {
        if (!tableView) return;
        const missing = issues
            .map((issue) => issue.logId)
            .filter((id) => !requestedRepliesRef.current.has(id));
        if (missing.length === 0) return;

        missing.forEach((id) => requestedRepliesRef.current.add(id));
        const generation = repliesGenerationRef.current;

        sendPostRequest(
            STATIONLOG_URL + "/replies/batch",
            missing,
            (response) => {
                if (generation !== repliesGenerationRef.current) return; // cache was cleared meanwhile
                const grouped = {};
                missing.forEach((id) => (grouped[id] = []));
                Object.assign(grouped, groupRepliesByRoot(response.data));
                setRepliesByRoot((current) => ({ ...current, ...grouped }));
            }
        );
    }, [tableView, issues, repliesByRoot]);

    const loadIssues = () => {
        if (selectedSite !== undefined) {
            if (selectedSite === "all") {
                sendPostRequest(
                    STATIONLOG_URL + "/search/all",
                    formatFilters(),
                    (response) => {
                        if (response.data.length === 0) {
                            setLoading(false);
                            setAtEnd(true);
                            return;
                        }
                        if (lastPage === 0) {
                            const sortedData = response.data.sort((a, b) =>
                                a.recordDate > b.recordDate ? -1 : 1
                            );
                            setIssues(sortedData);
                        } else {
                            const sortedData = issues
                                .concat(response.data)
                                .sort((a, b) =>
                                    a.recordDate > b.recordDate ? -1 : 1
                                );
                            setIssues(sortedData);
                        }
                        setLoading(false);
                    }
                );
            } else {
                sendPostRequest(
                    STATIONLOG_URL + "/search/" + selectedSite,
                    formatFilters(),
                    (response) => {
                        if (response.data.length === 0) {
                            setLoading(null);
                            setAtEnd(true);
                            return;
                        }
                        if (lastPage === 0) {
                            const sortedData = response.data.sort((a, b) =>
                                a.recordDate > b.recordDate ? -1 : 1
                            );
                            setIssues(sortedData);
                        } else {
                            const sortedData = issues
                                .concat(response.data)
                                .sort((a, b) =>
                                    a.recordDate > b.recordDate ? -1 : 1
                                );
                            setIssues(sortedData);
                        }
                        setLoading(false);
                    }
                );
            }
        }
    };

    const loadRecent = () => {
        if (selectedSite === "all") {
            sendPostRequest(
                STATIONLOG_URL + "/recent/all",
                { pageNumber: 0, pageSize: 10 },
                (response) => {
                    setRecentItems(
                        response.data.sort((a, b) =>
                            a.recordDate > b.recordDate ? -1 : 1
                        )
                    );
                }
            );
        } else {
            sendPostRequest(
                STATIONLOG_URL + "/recent/" + selectedSite,
                { pageNumber: 0, pageSize: 10 },
                (response) => {
                    setRecentItems(
                        response.data.sort((a, b) =>
                            a.recordDate > b.recordDate ? -1 : 1
                        )
                    );
                }
            );
        }
    };

    const clearRepliesCache = () => {
        repliesGenerationRef.current += 1;
        requestedRepliesRef.current = new Set();
        setRepliesByRoot({});
    };

    // Exports everything matching the current filters, not just what's loaded.
    const handleExportCsv = () => {
        const url =
            selectedSite === "all"
                ? STATIONLOG_URL + "/export/all"
                : STATIONLOG_URL + "/export/" + selectedSite;
        sendPostRequest(url, formatFilters(), (response) => {
            exportCsv(
                response.data.issues,
                groupRepliesByRoot(response.data.replies),
                filters,
                selectedSite
            );
        });
    };

    const refresh = () => {
        clearRepliesCache();
        loadIssues();
        loadRecent();
    };

    const formatFilters = () => {
        let formattedFilters = { ...filters };
        formattedFilters.people =
            formattedFilters.people.size === 0
                ? null
                : Array.from(formattedFilters.people);
        formattedFilters["pageNumber"] = lastPage;
        return formattedFilters;
    };

    const mapItemsToEntry = () => {
        return issues.map((issue, index) => (
            <StationLogEntry
                key={"station-log-entry-" + index}
                issue={issue}
                setSelectedIssue={setSelectedIssue}
            />
        ));
    };

    const handleScroll = (event) => {
        const atBottom =
            event.target.scrollHeight -
            event.target.scrollTop -
            event.target.clientHeight <=
            20;
        if (atBottom && loading === false && !atEnd) {
            setLastPage(lastPage + 1);
        }
    };

    const renderStationLog = () => {
        if (editorOpen) {
            return (
                <StationLogIssueEditor
                    selectedSite={selectedSite}
                    toggle={toggleEditorOpen}
                    refresh={refresh}
                />
            );
        } else if (selectedIssue === undefined) {
            return (
                <div id="station-log" onScroll={handleScroll}>
                    <h2>Station Log</h2>
                    <RecentItems
                        issues={recentItems}
                        setSelectedIssue={setSelectedIssue}
                        loading={loading}
                    />
                    <h3>All Entries</h3>
                    <StationLogSearch
                        filters={filters}
                        setFilters={setFilters}
                        selectedSite={selectedSite}
                        tableView={tableView}
                        setTableView={setTableView}
                    />
                    <div id="station-log-entries-section">
                        <div id="station-log-actions">
                            <StationLogCreateIssue toggle={toggleEditorOpen} />
                            {tableView && (
                                <Button
                                    id="station-log-download-csv"
                                    color="primary"
                                    onClick={handleExportCsv}
                                    disabled={issues.length === 0}
                                >
                                    Download CSV
                                </Button>
                            )}
                        </div>
                        <div id="station-log-entries">
                            {tableView ? (
                                <StationLogTable issues={issues} repliesByRoot={repliesByRoot} />
                            ) : (
                                mapItemsToEntry()
                            )}
                            <StationLogEntryPlaceholder
                                hidden={issues.length > 0 || loading}
                            />

                            <div
                                style={{
                                    maxWidth: "800px",
                                    width: "100%",
                                    textAlign: "center",
                                }}
                                hidden={!loading}
                            >
                                <Spinner style={{ margin: "auto" }} />
                            </div>
                        </div>
                    </div>
                    {renderControlledComponent(
                        <Button
                            id="station-log-new-issue-button"
                            color="primary"
                            onClick={toggleEditorOpen}
                        >
                            <img
                                className="inline-svg plus"
                                src={plus}
                                alt="Plus"
                            />
                        </Button>
                    )}
                </div>
            );
        } else {
            return (
                <StationLogIssue
                    selectedIssue={selectedIssue}
                    setSelectedIssue={setSelectedIssue}
                    refresh={refresh}
                />
            );
        }
    };

    return (
        <StationLogContext.Provider value={{ siteDetails }}>
            {renderStationLog()}
        </StationLogContext.Provider>
    );
}

function RecentItems({ issues, setSelectedIssue, loading }) {
    const mapItemsToRecentItem = () => {
        const mostRecent = issues.sort((a, b) =>
            a.recordDate > b.recordDate ? -1 : 1
        );
        return mostRecent.map((issue, index) => (
            <RecentItem
                key={"station-log-recent-item" + index}
                issue={issue}
                setSelectedIssue={setSelectedIssue}
            />
        ));
    };

    return (
        <div id="station-log-recent-issues" dir="ltr">
            <StationLogRecentPlaceholder
                hidden={issues.length > 0 || loading}
            />
            {mapItemsToRecentItem()}
        </div>
    );
}

function RecentItem({ issue, setSelectedIssue }) {
    const truncateText = (text, length) => {
        if (text.length <= length) return text;
        for (var i = length; i > length - 20; i--) {
            if (text.charAt(i) === " ") {
                return (
                    text.substring(0, i) +
                    (text.substring(i - 1, i).match(/\W/) ? " " : "") +
                    "..."
                );
            }
        }
        return (
            text.substring(0, length - 2) +
            (text.substring(length - 3, length - 2).match(/\W/) ? " " : "") +
            "..."
        );
    };

    const getTimestamp = () => {
        if (issue === undefined) return null;
        return formatSiteLst(issue.recordDate, issue.siteTimezone);
    };

    const handleClick = (event) => {
        setSelectedIssue(issue.rootEntry);
    };

    const renderItem = () => {
        if (issue === undefined) return null;
        if (issue.parentLogId === null) {
            return (
                <RecentIssue
                    title={truncateText(issue.title, 40)}
                    aqmetAbbr={issue.aqmetAbbr}
                    detail={truncateText(issue.detail, 100)}
                    timestamp={getTimestamp()}
                    author={issue.author}
                    authorEmail={issue.authorEmail}
                    subject={issue.title}
                    handleClick={handleClick}
                />
            );
        } else {
            return (
                <RecentReply
                    title={truncateText(issue.title, 40)}
                    aqmetAbbr={issue.aqmetAbbr}
                    detail={truncateText(issue.detail, 100)}
                    timestamp={getTimestamp()}
                    author={issue.author}
                    authorEmail={issue.authorEmail}
                    subject={issue.title}
                    handleClick={handleClick}
                />
            );
        }
    };

    return renderItem();
}

function RecentIssue({
    handleClick,
    title,
    aqmetAbbr,
    detail,
    author,
    authorEmail,
    subject,
    timestamp,
}) {
    return (
        <Card className="recent-issue" onClick={handleClick}>
            <CardTitle>
                <div className="recent-issue-title">{title}</div>
                <div className="recent-issue-subtitle">
                    <div className="recent-issue-abbr">{aqmetAbbr}</div>
                    <div className="recent-issue-type">New entry</div>
                </div>
            </CardTitle>
            <CardBody>
                <div className="recent-issue-detail">{detail}</div>
                <br />
                <div className="recent-issue-info">
                    <span>{timestamp}</span> by{" "}
                    <AuthorLink
                        className="station-log-username"
                        name={author}
                        email={authorEmail}
                        subject={subject}
                    />
                    <span>
                        {" "}
                        <img src={user} className="user-icon" alt="User Icon" />
                    </span>
                </div>
            </CardBody>
        </Card>
    );
}

function RecentReply({
    handleClick,
    title,
    aqmetAbbr,
    detail,
    timestamp,
    author,
    authorEmail,
    subject,
}) {
    return (
        <Card className="recent-reply" onClick={handleClick}>
            <CardTitle>
                <div className="recent-issue-title">{title}</div>
                <div className="recent-issue-subtitle">
                    <div className="recent-issue-abbr">{aqmetAbbr}</div>
                    <div className="recent-issue-type">New reply</div>
                </div>
            </CardTitle>
            <CardBody>
                <div className="station-log-reply-body">{detail}</div>
                <br />
                <div className="station-log-reply-info">
                    <span>{timestamp}</span> by{" "}
                    <AuthorLink
                        className="station-log-username"
                        name={author}
                        email={authorEmail}
                        subject={subject}
                    />
                    <img src={user} className="user-icon" alt="User Icon" />
                </div>
            </CardBody>
        </Card>
    );
}

function StationLogSearch({
    selectedSite,
    filters,
    setFilters,
    tableView,
    setTableView,
}) {
    const [people, setPeople] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);

    const { sendGetRequest } = useContext(RestContext);

    const toggleModal = () => setModalOpen(!modalOpen);
    const toggleTableView = () => setTableView(!tableView);

    useEffect(() => {
        if (selectedSite !== undefined) {
            if (selectedSite === "all") {
                sendGetRequest(STATIONLOG_URL + "/people/all", (response) => {
                    let data = response.data;
                    for (let index in data) {
                        if (data[index].fullName === "ARS Field Staff") {
                            data.splice(index, 1);
                            break;
                        }
                    }
                    data.sort((a, b) => (a.fullName > b.fullName ? 1 : -1));
                    setPeople(data);
                });
            } else {
                sendGetRequest(
                    STATIONLOG_URL + "/people/" + selectedSite,
                    (response) => {
                        let data = response.data;
                        for (let index in data) {
                            if (data[index].fullName === "ARS Field Staff") {
                                data.splice(index, 1);
                                break;
                            }
                        }
                        data.sort((a, b) => (a.fullName > b.fullName ? 1 : -1));
                        setPeople(data);
                    }
                );
            }
        }
    }, [selectedSite]);

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        if (value === "") {
            setFilters((values) => ({ ...values, [name]: null }));
        } else {
            setFilters((values) => ({ ...values, [name]: value }));
        }
    };

    const minDate = () => {
        return filters.startDate;
    };

    const maxDate = () => {
        if (filters.endDate === null) return today();
        return filters.endDate;
    };

    const handlePeople = (person) => {
        const value = Number(person.userId);
        let newPeople = new Set(filters.people);
        if (newPeople.has(value)) {
            newPeople.delete(value);
        } else {
            newPeople.add(value);
        }
        setFilters((values) => ({ ...values, people: newPeople }));
    };

    const personChecked = (person) => {
        if (filters === undefined) return false;
        const result = filters.people.has(person.userId);
        return result;
    };

    const mapPeopleToDropdownItem = () => {
        return people.map((person, index) => (
            <DropdownItem
                key={"people-dropdown-" + index}
                className="station-log-search-people-row"
                value={person.userId}
                onClick={() => {
                    handlePeople(person);
                }}
                toggle={false}
            >
                <span>{person.fullName}</span>
                <Input
                    type="checkbox"
                    value={personChecked(person)}
                    checked={personChecked(person)}
                    readOnly
                />
            </DropdownItem>
        ));
    };

    const mapPeopleToListItem = () => {
        return people.map((person, index) => (
            <div
                key={"people-list-" + index}
                className="station-log-search-mobile-people-item"
                onClick={() => {
                    handlePeople(person);
                }}
            >
                {person.fullName}
                <Input
                    type="checkbox"
                    value={personChecked(person)}
                    checked={personChecked(person)}
                    readOnly
                />
            </div>
        ));
    };

    const renderSearch = () => {
        if (checkMobile()) {
            return mobileSearch();
        } else {
            return desktopSearch();
        }
    };

    const clearSearchString = () => {
        setFilters((values) => ({ ...values, searchString: null }));
    };

    const searchStringEmpty = () => {
        if (filters === undefined) return true;
        if (filters.searchString === undefined || filters.searchString === null)
            return true;
        return false;
    };

    const getSearchString = () => {
        if (filters === undefined) return true;
        if (filters.searchString === undefined || filters.searchString === null)
            return "";
        return filters.searchString;
    };

    const desktopSearch = () => {
        return (
            <div id="station-log-search">
                <div id="station-log-search-input-wrapper">
                    <Input
                        id="station-log-search-input"
                        name="searchString"
                        type="text"
                        placeholder="Search..."
                        onChange={handleChange}
                        value={getSearchString()}
                    />
                    <Button
                        id="station-log-search-input-clear-button"
                        className="btn-close"
                        hidden={searchStringEmpty()}
                        onClick={clearSearchString}
                    />
                </div>
                <InputGroup id="station-log-search-date-range">
                    <InputGroupText>Dates</InputGroupText>
                    <Input
                        className="station-log-search-date"
                        name="startDate"
                        type="date"
                        onChange={handleChange}
                        max={maxDate()}
                        defaultValue={filters.startDate}
                    />
                    <Input
                        className="station-log-search-date"
                        name="endDate"
                        type="date"
                        onChange={handleChange}
                        min={minDate()}
                        max={today()}
                        defaultValue={filters.endDate}
                    />
                </InputGroup>
                <UncontrolledDropdown>
                    <DropdownToggle id="station-log-search-people-button">
                        People
                    </DropdownToggle>
                    <DropdownMenu style={{ maxHeight: 300, overflowY: "auto" }}>
                        {mapPeopleToDropdownItem()}
                    </DropdownMenu>
                </UncontrolledDropdown>
                <Button
                    id="station-log-search-table-button"
                    color="primary"
                    active={tableView}
                    onClick={toggleTableView}
                >
                    {tableView ? "List" : "Table"}
                </Button>
            </div>
        );
    };

    const mobileSearch = () => {
        return (
            <div id="station-log-search">
                <div id="station-log-search-input-wrapper">
                    <Input
                        id="station-log-search-input"
                        name="searchString"
                        type="text"
                        placeholder="Search..."
                        onChange={handleChange}
                        value={getSearchString()}
                    />
                    <Button
                        id="station-log-search-input-clear-button"
                        className="btn-close"
                        hidden={searchStringEmpty()}
                        onClick={clearSearchString}
                    />
                </div>
                <Button
                    id="station-log-search-filters-button"
                    color="primary"
                    onClick={toggleModal}
                >
                    Filters
                </Button>
                <Modal isOpen={modalOpen} toggle={toggleModal}>
                    <ModalHeader toggle={toggleModal}>Filters</ModalHeader>
                    <ModalBody>
                        <h4>Dates</h4>
                        <InputGroup id="station-log-search-date-range">
                            <Input
                                className="station-log-search-date"
                                name="startDate"
                                type="date"
                                onChange={handleChange}
                                max={maxDate()}
                                defaultValue={filters.startDate}
                            />
                            <Input
                                className="station-log-search-date"
                                name="endDate"
                                type="date"
                                onChange={handleChange}
                                min={minDate()}
                                max={today()}
                                defaultValue={filters.endDate}
                            />
                        </InputGroup>
                        <br />
                        <h4>People</h4>
                        <div id="station-log-search-mobile-people-list">
                            {mapPeopleToListItem()}
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        );
    };

    return renderSearch();
}

function StationLogEntry({ issue, setSelectedIssue }) {
    const getTimestamp = () => {
        if (issue === undefined) return null;
        return formatSiteLst(issue.recordDate, issue.siteTimezone);
    };

    const getAuthor = () => {
        if (issue === undefined) return null;
        return issue.author;
    };
    return (
        <div
            className="station-log-entry"
            onClick={() => setSelectedIssue(issue.logId)}
        >
            <div className="station-log-entry-title">{issue.title}</div>
            <div className="station-log-entry-abbr">{issue.siteAbbr}</div>
            <div className="station-log-entry-body">{issue.detail}</div>
            <div className="station-log-entry-info">
                <span>{getTimestamp()}</span> by{" "}
                <AuthorLink
                    className="station-log-username"
                    name={getAuthor()}
                    email={issue.authorEmail}
                    subject={issue.title}
                />
                <span>
                    {" "}
                    <img src={user} className="user-icon" alt="User Icon" />
                </span>
            </div>
        </div>
    );
}

function StationLogCreateIssue({ toggle }) {
    return (
        <div id="station-log-create-issue" onClick={toggle}>
            Create New Entry
        </div>
    );
}

function StationLogRecentPlaceholder({ hidden }) {
    return (
        <div id="recent-placeholder" hidden={hidden}>
            <span>No entries found.</span>
        </div>
    );
}

function StationLogEntryPlaceholder({ hidden }) {
    return (
        <div id="station-log-entry-placeholder" hidden={hidden}>
            <span>No entries found.</span>
        </div>
    );
}

function StationLogTable({ issues, repliesByRoot }) {
    const rows = useMemo(
        () => getThreadRows(issues, repliesByRoot, true),
        [issues, repliesByRoot]
    );

    return (
        <div className="station-log-table-wrapper">
            <Table size="sm" responsive>
                <thead>
                    <tr>
                        {CSV_HEADERS.map((h) => (
                            <th key={h}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr
                            key={row.key}
                            className={
                                row.type === "Entry"
                                    ? "station-log-table-entry-row"
                                    : undefined
                            }
                        >
                            {getCells(row).map((cell, i) => (
                                <td key={i}>{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
}

export default StationLog;